import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { enUS, sv } from "date-fns/locale";
import { MessageSquare, Plus, Search } from "lucide-react";

import i18n from "@/i18n";
import { cn } from "@/lib/utils";
import { shortcutLabel } from "@/lib/keyboard";
import { useSalesInsightThreadsAll } from "@/hooks/queries/use-sales-insight-threads";

interface Props {
  businessId: string;
  activeThreadId: string | null;
  onSelect: (threadId: string) => void;
  onNewChat: () => void;
}

/**
 * The thread-picker popover (4b iii) under the rail title — search + recent
 * threads + a "New chat" footer. Absorbs the old sidebar `NavChats` list.
 */
export function CoachThreadPicker({
  businessId,
  activeThreadId,
  onSelect,
  onNewChat,
}: Props) {
  const { t } = useTranslation("intelligence");
  const [query, setQuery] = useState("");
  const threadsQuery = useSalesInsightThreadsAll(businessId || undefined);
  const threads = threadsQuery.data ?? [];

  const locale = i18n.language.split("-")[0] === "sv" ? sv : enUS;

  const filtered = useMemo(() => {
    const all = threadsQuery.data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((thread) =>
      (thread.title ?? "").toLowerCase().includes(q),
    );
  }, [threadsQuery.data, query]);

  return (
    <div className="flex max-h-[420px] w-[300px] flex-col">
      <div className="border-b border-g-line p-2">
        <div className="flex items-center gap-2 rounded-g-sm bg-g-bg-input px-2.5 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-g-ring">
          <Search
            aria-hidden="true"
            className="size-[15px] shrink-0 text-g-ink-400"
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t("rail.picker.searchLabel")}
            placeholder={t("rail.picker.search", { count: threads.length })}
            className="h-8 w-full bg-transparent text-[13px] text-g-ink-900 outline-none placeholder:text-g-ink-400"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-1">
        {threadsQuery.isLoading ? (
          <div
            role="status"
            className="px-2 py-6 text-center text-[12.5px] text-g-ink-400"
          >
            {t("threads.loading")}
          </div>
        ) : threadsQuery.isError ? (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 px-2 py-6 text-center text-[12.5px] text-g-ink-400"
          >
            <span>{t("threads.loadError")}</span>
            <button
              type="button"
              onClick={() => void threadsQuery.refetch()}
              disabled={threadsQuery.isFetching}
              className="rounded-g-sm border border-g-line bg-g-bg-card px-2.5 py-1 text-[12.5px] font-medium text-g-ink-900 transition-colors hover:bg-g-primary-wash disabled:opacity-60"
            >
              {t("threads.retry")}
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-2 py-6 text-center text-[12.5px] text-g-ink-400">
            {query.trim() ? t("scope.picker.noResults") : t("threads.empty")}
          </div>
        ) : (
          filtered.map((thread) => {
            const isActive = thread.id === activeThreadId;
            const title = thread.title?.trim() || t("threads.untitled");
            const updated = thread.updatedAt
              ? new Date(thread.updatedAt)
              : null;
            return (
              <button
                key={thread.id}
                type="button"
                onClick={() => onSelect(thread.id)}
                aria-current={isActive ? "true" : undefined}
                className={cn(
                  "flex w-full items-start gap-2 rounded-g-sm px-2 py-1.5 text-left transition-colors",
                  isActive
                    ? "bg-g-primary-tint text-g-primary-press"
                    : "text-g-ink-900 hover:bg-g-primary-wash",
                )}
              >
                <MessageSquare
                  aria-hidden="true"
                  className={cn(
                    "mt-0.5 size-[15px] shrink-0",
                    isActive ? "text-g-primary-press" : "text-g-ink-400",
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium leading-snug">
                    {title}
                  </span>
                  {updated && (
                    <span className="block text-[11px] text-g-ink-400">
                      {formatDistanceToNow(updated, {
                        addSuffix: true,
                        locale,
                      })}
                    </span>
                  )}
                </span>
              </button>
            );
          })
        )}
      </div>

      <div className="border-t border-g-line p-1">
        <button
          type="button"
          onClick={onNewChat}
          className="flex w-full items-center gap-2 rounded-g-sm px-2 py-1.5 text-left text-[13px] font-medium text-g-ink-900 transition-colors hover:bg-g-primary-wash"
        >
          <Plus
            aria-hidden="true"
            className="size-[15px] shrink-0 text-g-ink-400"
          />
          <span className="flex-1">{t("threads.new")}</span>
          <kbd className="pointer-events-none select-none rounded-g-xs border border-g-line bg-g-bg-track px-1.5 font-mono text-[10.5px] font-medium text-g-ink-400">
            {shortcutLabel("J")}
          </kbd>
        </button>
      </div>
    </div>
  );
}
